import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Modal, Header, Button, Dropdown, Message, List, Checkbox } from 'semantic-ui-react';

const AssignParentsModal = ({
    open,
    setOpen,
    optionItem,
    existingOptions,
    setSurveyOptions,
    surveyStrucRow,
    setSurveyStrucRow,
    setUpdateJsonFile,
}) => {
    const [parentCategoryId, setParentCategoryId] = useState('');
    const [parentOptionId, setParentOptionId] = useState('');
    const [pasteText, setPasteText] = useState('');
    const [mode, setMode] = useState('assign');
    const [errorText, setErrorText] = useState('');
    const [selectedIds, setSelectedIds] = useState(new Set());

    const categoryChoices = useMemo(() => {
        const rows = Array.isArray(surveyStrucRow) ? surveyStrucRow : [];
        return rows
            .filter((c) => c && c.id)
            .map((c) => ({ key: c.id, text: `${c.name} (${c.id})`, value: c.id }));
    }, [surveyStrucRow]);

    const parentOptions = useMemo(() => {
        if (!parentCategoryId) return [];
        const rows = Array.isArray(surveyStrucRow) ? surveyStrucRow : [];
        const parentCat = rows.find((c) => c.id === parentCategoryId);
        const list = Array.isArray(parentCat?.options) ? parentCat.options : [];
        return list
            .map((o) => ({ key: o.id, text: `${o.name} (${o.id})`, value: o.id }))
            .sort((a, b) => a.text.localeCompare(b.text));
    }, [surveyStrucRow, parentCategoryId]);

    const nameToOptionInCurrent = useMemo(() => {
        const base = Array.isArray(existingOptions) ? existingOptions : [];
        const map = new Map();
        base.forEach((o) => {
            const key = (o?.name || '').trim().toLowerCase();
            if (key && !map.has(key)) map.set(key, o);
        });
        return map;
    }, [existingOptions]);

    const parsedNames = useMemo(() => {
        if (!pasteText || pasteText.trim().length === 0) return [];
        const lines = pasteText
            .split(/\r?\n/)
            .map((l) => l.trim())
            .filter((l) => l.length > 0);
        const names = [];
        for (const line of lines) {
            // Accept CSV or TSV; take first column as the option name
            const parts = line.split(/\t|,/);
            const name = (parts[0] || '').trim();
            if (name.length > 0) names.push(name);
        }
        return names;
    }, [pasteText]);

    const matchResult = useMemo(() => {
        const matched = [];
        const unmatched = [];
        const used = new Set();
        parsedNames.forEach((n) => {
            const key = n.trim().toLowerCase();
            const found = nameToOptionInCurrent.get(key);
            if (found && !used.has(found.id)) {
                used.add(found.id);
                matched.push(found);
            } else {
                unmatched.push(n);
            }
        });
        return { matched, unmatched };
    }, [parsedNames, nameToOptionInCurrent]);

    // Compute options eligible for assignment: unassigned OR assigned to the currently selected parent
    const eligibleOptionsForAssign = useMemo(() => {
        const base = Array.isArray(existingOptions) ? existingOptions : [];
        return base.filter((o) => {
            const hasParent = o && o.parent_group_id && o.parent_id;
            if (!hasParent) return true;
            return (
                o.parent_group_id === parentCategoryId && o.parent_id === parentOptionId
            );
        });
    }, [existingOptions, parentCategoryId, parentOptionId]);

    // Sync checkbox selection with pasted matches when in assign mode
    React.useEffect(() => {
        if (mode !== 'assign') return;
        if (parsedNames.length === 0) return; // do not clear manual selections when nothing pasted
        const eligibleIds = new Set(eligibleOptionsForAssign.map((o) => o.id));
        const next = new Set();
        matchResult.matched.forEach((o) => {
            if (eligibleIds.has(o.id)) next.add(o.id);
        });
        setSelectedIds(next);
    }, [mode, parsedNames, matchResult, eligibleOptionsForAssign]);

    // Prune selections when eligibility changes (e.g., parent changed)
    React.useEffect(() => {
        const eligibleIds = new Set(eligibleOptionsForAssign.map((o) => o.id));
        let changed = false;
        const next = new Set();
        selectedIds.forEach((id) => {
            if (eligibleIds.has(id)) next.add(id);
            else changed = true;
        });
        if (changed) setSelectedIds(next);
    }, [eligibleOptionsForAssign]);

    const assignedInCurrent = useMemo(() => {
        const base = Array.isArray(existingOptions) ? existingOptions : [];
        return base.filter((o) => o && o.parent_group_id && o.parent_id);
    }, [existingOptions]);

    function toggleSelected(id) {
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedIds(next);
    }

    function resolveParentDisplay(option) {
        if (!option?.parent_group_id || !option?.parent_id) return '';
        const rows = Array.isArray(surveyStrucRow) ? surveyStrucRow : [];
        const cat = rows.find((c) => c.id === option.parent_group_id);
        const parentOpt = Array.isArray(cat?.options) ? cat.options.find((o) => o.id === option.parent_id) : null;
        const catName = cat?.name || option.parent_group_id;
        const optName = parentOpt?.name || option.parent_id;
        return `${catName} > ${optName}`;
    }

    function removeSingle(id) {
        const base = Array.isArray(existingOptions) ? existingOptions : [];
        const updatedList = base.map((o) => (o.id === id ? { ...o, parent_group_id: undefined, parent_id: undefined } : o));
        setSurveyOptions(updatedList);
        const excluded = (surveyStrucRow || []).filter((sc) => sc.id !== optionItem.id);
        const updatedItem = { ...optionItem, options: updatedList };
        excluded.push(updatedItem);
        setSurveyStrucRow(excluded);
        setUpdateJsonFile(Math.random());
    }

    function clearAllAssigned() {
        if (assignedInCurrent.length === 0) return;
        const base = Array.isArray(existingOptions) ? existingOptions : [];
        const ids = new Set(assignedInCurrent.map((o) => o.id));
        const updatedList = base.map((o) => (ids.has(o.id) ? { ...o, parent_group_id: undefined, parent_id: undefined } : o));
        setSurveyOptions(updatedList);
        const excluded = (surveyStrucRow || []).filter((sc) => sc.id !== optionItem.id);
        const updatedItem = { ...optionItem, options: updatedList };
        excluded.push(updatedItem);
        setSurveyStrucRow(excluded);
        setUpdateJsonFile(Math.random());
    }

    function onClose() {
        setOpen(false);
    }

    function applyChanges() {
        setErrorText('');
        if (mode === 'assign') {
            if (!parentCategoryId || !parentOptionId) {
                setErrorText('Select a parent category and parent option first.');
                return;
            }
        }
        const base = Array.isArray(existingOptions) ? existingOptions : [];
        const idsToUpdate = new Set(selectedIds);
        if (idsToUpdate.size === 0) {
            setErrorText('Select at least one option to update.');
            return;
        }

        const updatedList = base.map((o) => {
            if (!idsToUpdate.has(o.id)) return o;
            if (mode === 'assign') {
                return {
                    ...o,
                    parent_group_id: parentCategoryId,
                    parent_id: parentOptionId,
                };
            }
            // remove
            return {
                ...o,
                parent_group_id: undefined,
                parent_id: undefined,
            };
        });

        // Update local list for immediate UI feedback
        setSurveyOptions(updatedList);

        // Persist into categories JSON
        const excluded = (surveyStrucRow || []).filter((sc) => sc.id !== optionItem.id);
        const updatedItem = { ...optionItem, options: updatedList };
        excluded.push(updatedItem);
        setSurveyStrucRow(excluded);
        setUpdateJsonFile(Math.random());
    }

    return (
        <Modal open={open} onClose={onClose} size="large">
            <Modal.Header>
                <Header as="h2">
                    Assign Parents
                    <Header.Subheader>
                        Select a parent (category and option), paste option names and/or use the checkbox list to choose options. Use Remove mode to clear existing parents.
                    </Header.Subheader>
                </Header>
            </Modal.Header>
            <Modal.Content scrolling>
                {errorText && (
                    <Message negative header="Cannot proceed" content={errorText} />
                )}

                <Row>
                    <FieldLabel>Parent Category</FieldLabel>
                    <Dropdown
                        selection
                        search
                        fluid
                        placeholder="Select parent category"
                        options={categoryChoices}
                        value={parentCategoryId}
                        onChange={(e, { value }) => {
                            setParentCategoryId(value);
                            setParentOptionId('');
                        }}
                    />
                </Row>
                <Row>
                    <FieldLabel>Parent Option</FieldLabel>
                    <Dropdown
                        selection
                        search
                        fluid
                        placeholder="Select parent option"
                        options={parentOptions}
                        value={parentOptionId}
                        onChange={(e, { value }) => setParentOptionId(value)}
                        disabled={!parentCategoryId}
                    />
                </Row>

                <Row>
                    <FieldLabel>Mode</FieldLabel>
                    <Button.Group>
                        <Button
                            color={mode === 'assign' ? 'violet' : undefined}
                            onClick={() => setMode('assign')}
                        >
                            Assign
                        </Button>
                        <Button.Or />
                        <Button
                            color={mode === 'remove' ? 'red' : undefined}
                            onClick={() => setMode('remove')}
                        >
                            Remove
                        </Button>
                    </Button.Group>
                </Row>

                {mode === 'assign' && (
                    <>
                        <Row>
                            <FieldLabel>Paste option names (CSV/TSV supported; first column = name)</FieldLabel>
                            <TextArea
                                placeholder={"e.g.\nRole 1\nRole 2\nRole 3"}
                                value={pasteText}
                                onChange={(e) => setPasteText(e.target.value)}
                            />
                        </Row>

                        <Stats>
                            Parsed names: <b>{parsedNames.length}</b> | Matched in this category: <b>{matchResult.matched.length}</b> | Unmatched: <b>{matchResult.unmatched.length}</b>
                            {matchResult.unmatched.length > 0 && (
                                <Preview>
                                    Unmatched (first 10): {matchResult.unmatched.slice(0, 10).join(', ')}
                                </Preview>
                            )}
                        </Stats>

                        <Row>
                            <FieldLabel>Options in this category (check to include in assignment)</FieldLabel>
                            <OptionsList>
                                <List divided relaxed>
                                    {(eligibleOptionsForAssign || []).map((opt) => (
                                        <List.Item key={opt.id}>
                                            <List.Content floated="right">
                                                <Checkbox
                                                    checked={selectedIds.has(opt.id)}
                                                    onChange={() => toggleSelected(opt.id)}
                                                />
                                            </List.Content>
                                            <List.Content>
                                                <List.Header>{opt.name}</List.Header>
                                                <List.Description style={{ color: '#888' }}>{opt.id}</List.Description>
                                            </List.Content>
                                        </List.Item>
                                    ))}
                                </List>
                            </OptionsList>
                        </Row>
                    </>
                )}

                {mode === 'remove' && (
                    <>
                        <Row>
                            <FieldLabel>Currently assigned options</FieldLabel>
                            {assignedInCurrent.length === 0 ? (
                                <NoneHint>No options have parents assigned.</NoneHint>
                            ) : (
                                <OptionsList>
                                    <List divided relaxed>
                                        {assignedInCurrent.map((opt) => (
                                            <List.Item key={opt.id}>
                                                <List.Content floated="right">
                                                    <Button size="tiny" color="red" onClick={() => removeSingle(opt.id)}>Remove</Button>
                                                </List.Content>
                                                <List.Content>
                                                    <List.Header>{opt.name}</List.Header>
                                                    <List.Description style={{ color: '#888' }}>{resolveParentDisplay(opt)}</List.Description>
                                                </List.Content>
                                            </List.Item>
                                        ))}
                                    </List>
                                </OptionsList>
                            )}
                        </Row>
                        {assignedInCurrent.length > 0 && (
                            <Row>
                                <Button color="red" onClick={clearAllAssigned}>Clear All</Button>
                            </Row>
                        )}
                    </>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}>Close</Button>
                {mode === 'assign' ? (
                    <Button
                        color="violet"
                        onClick={applyChanges}
                        disabled={selectedIds.size === 0 || (!parentCategoryId || !parentOptionId)}
                    >
                        Assign Parent
                    </Button>
                ) : null}
            </Modal.Actions>
        </Modal>
    );
};

export default AssignParentsModal;

const Row = styled.div`
    margin-bottom: 12px;
`;

const FieldLabel = styled.div`
    margin-bottom: 6px;
    font-weight: bold;
`;

const TextArea = styled.textarea`
    width: 100%;
    min-height: 180px;
    font-family: monospace;
    padding: 10px;
`;

const Stats = styled.div`
    margin-top: 8px;
`;

const Preview = styled.div`
    margin-top: 6px;
`;

const OptionsList = styled.div`
    max-height: 340px;
    overflow: auto;
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 6px;
`;

const NoneHint = styled.div`
    color: #888;
`;


